import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { TenantBrandingProvider } from './context/TenantBrandingContext';
import { HostnameTenantProvider, useHostnameTenant } from './context/HostnameTenantContext';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import RoleGuard from './components/RoleGuard';
import Navigation from './components/Navigation';
import ToastNotification from './components/ToastNotification';
import PageLoadingSpinner from './components/PageLoadingSpinner';
import HostnameTenantBootstrapGate from './components/HostnameTenantBootstrapGate';
import CleanUrlRedirector from './components/CleanUrlRedirector';
import InstallAppBanner from './components/InstallAppBanner';
import CookieConsentBanner from './components/CookieConsentBanner';
import DeepLinkHandler from './components/DeepLinkHandler';
import { DEFAULT_TENANT_SLUG } from './hooks/useTenant';
import './App.css';

const LoginPage = React.lazy(() => import(/* webpackChunkName: "login" */ './pages/LoginPage'));
const RegisterPage = React.lazy(() => import(/* webpackChunkName: "register" */ './pages/RegisterPage'));
const Dashboard = React.lazy(() => import(/* webpackChunkName: "dashboard" */ './pages/Dashboard'));
const LandlordDataPage = React.lazy(() => import(/* webpackChunkName: "landlord" */ './pages/LandlordDataPage'));
const PropertiesPage = React.lazy(() => import(/* webpackChunkName: "properties" */ './pages/PropertiesPage'));
const BankAccountsPage = React.lazy(() => import(/* webpackChunkName: "bank-accounts" */ './pages/BankAccountsPage'));
const ContractsPage = React.lazy(() => import(/* webpackChunkName: "contracts" */ './pages/ContractsPage'));
const ContractWizard = React.lazy(() => import(/* webpackChunkName: "contract-wizard" */ './pages/ContractWizard'));
const ContractImportPage = React.lazy(() => import(/* webpackChunkName: "contract-import" */ './pages/ContractImportPage'));
const OAuthCallback = React.lazy(() => import(/* webpackChunkName: "oauth-callback" */ './pages/OAuthCallback'));
const ForgotPasswordPage = React.lazy(() => import(/* webpackChunkName: "forgot-password" */ './pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import(/* webpackChunkName: "reset-password" */ './pages/ResetPasswordPage'));
const VerifyEmailPage = React.lazy(() => import(/* webpackChunkName: "verify-email" */ './pages/VerifyEmailPage'));
const LandingPage = React.lazy(() => import(/* webpackChunkName: "landing" */ './pages/LandingPage'));
const PrivacyPage = React.lazy(() => import(/* webpackChunkName: "privacy" */ './pages/PrivacyPage'));
const TermsPage = React.lazy(() => import(/* webpackChunkName: "terms" */ './pages/TermsPage'));
const McpPage = React.lazy(() => import(/* webpackChunkName: "mcp" */ './pages/McpPage'));
const AccountPage = React.lazy(() => import(/* webpackChunkName: "account" */ './pages/AccountPage'));
const UtilityBillsPage = React.lazy(() => import(/* webpackChunkName: "utility-bills" */ './pages/UtilityBillsPage'));
const UsersPage = React.lazy(() => import(/* webpackChunkName: "users" */ './pages/UsersPage'));
const CorrespondencePage = React.lazy(() => import(/* webpackChunkName: "correspondence" */ './pages/CorrespondencePage'));
const RentOutPage = React.lazy(() => import(/* webpackChunkName: "rent-out" */ './pages/RentOutPage'));
const ContractHistoryPage = React.lazy(() => import(/* webpackChunkName: "contract-history" */ './pages/ContractHistoryPage'));
const BookingsPage = React.lazy(() => import(/* webpackChunkName: "payments" */ './pages/BookingsPage'));
const LoansPage = React.lazy(() => import(/* webpackChunkName: "loans" */ './pages/LoansPage'));
const ZaehlerPage = React.lazy(() => import(/* webpackChunkName: "meter-management" */ './pages/ZaehlerPage'));
const ObjektkostenPage = React.lazy(() => import(/* webpackChunkName: "objektkosten" */ './pages/ObjektkostenPage'));
const SteuerPage = React.lazy(() => import(/* webpackChunkName: "steuer" */ './pages/SteuerPage'));
const StatistikPage = React.lazy(() => import(/* webpackChunkName: "statistik" */ './pages/StatistikPage'));

/** Rollen, die Zugriff auf Vermieter-exklusive Seiten erhalten */
const LANDLORD_ROLES = ['OWNER', 'ADMIN', 'MEMBER'];

const TenantLayout = () => (
  <>
    <a href="#main-content" className="skip-link">
      Zum Hauptinhalt springen
    </a>

    <header role="banner">
      <Navigation />
    </header>

    <ToastNotification />

    <main id="main-content" role="main" tabIndex={-1}>
      <React.Suspense fallback={<PageLoadingSpinner />}>
        <Outlet />
      </React.Suspense>
    </main>
  </>
);

/**
 * Routing tree that adapts based on hostname resolution:
 * - 'matched': clean URLs without /:tenantSlug prefix
 * - 'unmatched': existing slug-prefixed URLs (no regressions)
 *
 * Route children are inlined at each call-site rather than extracted into a
 * shared component to avoid the fragile "component returning bare <Route>
 * elements" anti-pattern in React Router v6.
 *
 * Must be rendered inside HostnameTenantProvider so useHostnameTenant() works.
 */
const AppRoutes = () => {
  const { status } = useHostnameTenant();

  if (status === 'matched') {
    // Clean-URL mode: routes are top-level, no /:tenantSlug prefix.
    // Also register the slug-prefixed routes so old bookmarks redirect cleanly.
    return (
      <Routes>
        {/* Clean URL routes */}
        <Route path="/">
          {/* Landing Page - eigenständiges Layout ohne App-Navigation */}
          <Route index element={
            <React.Suspense fallback={<PageLoadingSpinner />}>
              <LandingPage />
            </React.Suspense>
          } />

          {/* Datenschutzerklärung - eigenständiges Layout */}
          <Route path="datenschutz" element={
            <React.Suspense fallback={<PageLoadingSpinner />}>
              <PrivacyPage />
            </React.Suspense>
          } />

          {/* AGB - eigenständiges Layout */}
          <Route path="agb" element={
            <React.Suspense fallback={<PageLoadingSpinner />}>
              <TermsPage />
            </React.Suspense>
          } />

          {/* KI-Anbindung per MCP - eigenständiges Layout */}
          <Route path="mcp" element={
            <React.Suspense fallback={<PageLoadingSpinner />}>
              <McpPage />
            </React.Suspense>
          } />

          {/* App-Seiten mit Navigation */}
          <Route element={<TenantLayout />}>
            <Route path="login" element={<LoginPage />} />
            <Route path="register" element={<RegisterPage />} />
            <Route path="forgot-password" element={<ForgotPasswordPage />} />
            <Route path="reset-password" element={<ResetPasswordPage />} />
            <Route path="verify-email" element={<VerifyEmailPage />} />
            <Route path="auth/oauth/callback" element={<OAuthCallback />} />
            <Route path="dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="landlord" element={<ProtectedRoute><RoleGuard allow={LANDLORD_ROLES}><LandlordDataPage /></RoleGuard></ProtectedRoute>} />
            <Route path="properties" element={<ProtectedRoute><RoleGuard allow={LANDLORD_ROLES}><PropertiesPage /></RoleGuard></ProtectedRoute>} />
            <Route path="bank-accounts" element={<ProtectedRoute><RoleGuard allow={LANDLORD_ROLES}><BankAccountsPage /></RoleGuard></ProtectedRoute>} />
            <Route path="account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
            <Route path="users" element={<ProtectedRoute><RoleGuard allow={LANDLORD_ROLES}><UsersPage /></RoleGuard></ProtectedRoute>} />
            {/* AP-17-1: statische keys erzwingen einen Remount bei new↔edit-Navigation
                (React würde bei gleichem Element-Typ sonst dieselbe Wizard-Instanz
                wiederverwenden — savedContractIdRef bliebe dann auf dem alten Vertrag). */}
            <Route path="contracts/new" element={<ProtectedRoute><RoleGuard allow={LANDLORD_ROLES}><ContractWizard key="wizard-new" /></RoleGuard></ProtectedRoute>} />
            <Route path="contracts/import" element={<ProtectedRoute><RoleGuard allow={LANDLORD_ROLES}><ContractImportPage /></RoleGuard></ProtectedRoute>} />
            <Route path="contracts/:contractId/edit" element={<ProtectedRoute><RoleGuard allow={LANDLORD_ROLES}><ContractWizard key="wizard-edit" /></RoleGuard></ProtectedRoute>} />
            <Route path="contracts/:contractId/history" element={<ProtectedRoute><ContractHistoryPage /></ProtectedRoute>} />
            <Route path="contracts" element={<ProtectedRoute><ContractsPage /></ProtectedRoute>} />
            <Route path="utility-bills" element={<ProtectedRoute><UtilityBillsPage /></ProtectedRoute>} />
            <Route path="correspondence" element={<ProtectedRoute><CorrespondencePage /></ProtectedRoute>} />
            <Route path="meter-management" element={<ProtectedRoute><ZaehlerPage /></ProtectedRoute>} />
            <Route path="rent-out" element={<ProtectedRoute><RoleGuard allow={LANDLORD_ROLES}><RentOutPage /></RoleGuard></ProtectedRoute>} />
            <Route path="payments" element={<ProtectedRoute><RoleGuard allow={LANDLORD_ROLES}><BookingsPage /></RoleGuard></ProtectedRoute>} />
            <Route path="loans" element={<ProtectedRoute><RoleGuard allow={LANDLORD_ROLES}><LoansPage /></RoleGuard></ProtectedRoute>} />
            <Route path="objektkosten" element={<ProtectedRoute><RoleGuard allow={LANDLORD_ROLES}><ObjektkostenPage /></RoleGuard></ProtectedRoute>} />
            <Route path="steuer" element={<ProtectedRoute><RoleGuard allow={LANDLORD_ROLES}><SteuerPage /></RoleGuard></ProtectedRoute>} />
            <Route path="statistik" element={<ProtectedRoute><RoleGuard allow={LANDLORD_ROLES}><StatistikPage /></RoleGuard></ProtectedRoute>} />
          </Route>
        </Route>

        {/* Catch slug-prefixed URLs and redirect to clean form.
            CleanUrlRedirector reads the current location and navigates. */}
        <Route path="/:tenantSlug/*" element={<CleanUrlRedirector />} />
      </Routes>
    );
  }

  // Slug-prefixed mode (default / unmatched / loading won't reach here because
  // HostnameTenantBootstrapGate blocks until status is resolved).
  return (
    <Routes>
      <Route path="/:tenantSlug">
        {/* Landing Page - eigenständiges Layout ohne App-Navigation */}
        <Route index element={
          <React.Suspense fallback={<PageLoadingSpinner />}>
            <LandingPage />
          </React.Suspense>
        } />

        {/* Datenschutzerklärung - eigenständiges Layout */}
        <Route path="datenschutz" element={
          <React.Suspense fallback={<PageLoadingSpinner />}>
            <PrivacyPage />
          </React.Suspense>
        } />

        {/* AGB - eigenständiges Layout */}
        <Route path="agb" element={
          <React.Suspense fallback={<PageLoadingSpinner />}>
            <TermsPage />
          </React.Suspense>
        } />

        {/* KI-Anbindung per MCP - eigenständiges Layout */}
        <Route path="mcp" element={
          <React.Suspense fallback={<PageLoadingSpinner />}>
            <McpPage />
          </React.Suspense>
        } />

        {/* App-Seiten mit Navigation */}
        <Route element={<TenantLayout />}>
          <Route path="login" element={<LoginPage />} />
          <Route path="register" element={<RegisterPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="reset-password" element={<ResetPasswordPage />} />
          <Route path="verify-email" element={<VerifyEmailPage />} />
          <Route path="auth/oauth/callback" element={<OAuthCallback />} />
          <Route path="dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="landlord" element={<ProtectedRoute><RoleGuard allow={LANDLORD_ROLES}><LandlordDataPage /></RoleGuard></ProtectedRoute>} />
          <Route path="properties" element={<ProtectedRoute><RoleGuard allow={LANDLORD_ROLES}><PropertiesPage /></RoleGuard></ProtectedRoute>} />
          <Route path="bank-accounts" element={<ProtectedRoute><RoleGuard allow={LANDLORD_ROLES}><BankAccountsPage /></RoleGuard></ProtectedRoute>} />
          <Route path="account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
          <Route path="users" element={<ProtectedRoute><RoleGuard allow={LANDLORD_ROLES}><UsersPage /></RoleGuard></ProtectedRoute>} />
          {/* AP-17-1: statische keys erzwingen einen Remount bei new↔edit-Navigation
              (React würde bei gleichem Element-Typ sonst dieselbe Wizard-Instanz
              wiederverwenden — savedContractIdRef bliebe dann auf dem alten Vertrag). */}
          <Route path="contracts/new" element={<ProtectedRoute><RoleGuard allow={LANDLORD_ROLES}><ContractWizard key="wizard-new" /></RoleGuard></ProtectedRoute>} />
          <Route path="contracts/import" element={<ProtectedRoute><RoleGuard allow={LANDLORD_ROLES}><ContractImportPage /></RoleGuard></ProtectedRoute>} />
          <Route path="contracts/:contractId/edit" element={<ProtectedRoute><RoleGuard allow={LANDLORD_ROLES}><ContractWizard key="wizard-edit" /></RoleGuard></ProtectedRoute>} />
          <Route path="contracts/:contractId/history" element={<ProtectedRoute><ContractHistoryPage /></ProtectedRoute>} />
          <Route path="contracts" element={<ProtectedRoute><ContractsPage /></ProtectedRoute>} />
          <Route path="utility-bills" element={<ProtectedRoute><UtilityBillsPage /></ProtectedRoute>} />
          <Route path="correspondence" element={<ProtectedRoute><CorrespondencePage /></ProtectedRoute>} />
          <Route path="meter-management" element={<ProtectedRoute><ZaehlerPage /></ProtectedRoute>} />
          <Route path="rent-out" element={<ProtectedRoute><RoleGuard allow={LANDLORD_ROLES}><RentOutPage /></RoleGuard></ProtectedRoute>} />
          <Route path="payments" element={<ProtectedRoute><RoleGuard allow={LANDLORD_ROLES}><BookingsPage /></RoleGuard></ProtectedRoute>} />
          <Route path="loans" element={<ProtectedRoute><RoleGuard allow={LANDLORD_ROLES}><LoansPage /></RoleGuard></ProtectedRoute>} />
          <Route path="objektkosten" element={<ProtectedRoute><RoleGuard allow={LANDLORD_ROLES}><ObjektkostenPage /></RoleGuard></ProtectedRoute>} />
          <Route path="steuer" element={<ProtectedRoute><RoleGuard allow={LANDLORD_ROLES}><SteuerPage /></RoleGuard></ProtectedRoute>} />
          <Route path="statistik" element={<ProtectedRoute><RoleGuard allow={LANDLORD_ROLES}><StatistikPage /></RoleGuard></ProtectedRoute>} />
        </Route>
      </Route>
      <Route path="/" element={<Navigate to={`/${DEFAULT_TENANT_SLUG}`} replace />} />
      <Route path="*" element={<Navigate to={`/${DEFAULT_TENANT_SLUG}`} replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <AuthProvider>
          <Router>
            <HostnameTenantProvider>
              <HostnameTenantBootstrapGate>
                <TenantBrandingProvider>
                  <AppRoutes />
                  <DeepLinkHandler />
                  {/* Als letztes Kind: CookieConsentBanner braucht useLocation() und
                      muss deshalb innerhalb des Routers hängen. Als letztes
                      DOM-Element bleibt der Skip-Link aus TenantLayout weiterhin der
                      erste Tab-Stopp der Seite (WCAG 2.4.1) — dieselbe Überlegung wie
                      beim InstallAppBanner unten. */}
                  <CookieConsentBanner />
                  {/* Beide Fixed-Bottom-Banner liegen nach dem gerouteten
                      Seiteninhalt. Der Skip-Link bleibt damit der erste
                      Tab-Stopp; InstallAppBanner kann zugleich useLocation()
                      fuer eine kollisionsfreie Anzeige verwenden. */}
                  <InstallAppBanner />
                </TenantBrandingProvider>
              </HostnameTenantBootstrapGate>
            </HostnameTenantProvider>
          </Router>
        </AuthProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
